import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';

// Where salons fill in the details their documents need (Settings > Documents).
const SETTINGS_DOCUMENTS_PATH = '/settings/documents';

// Display labels for categories (keep in step with the library cards).
const CATEGORY_LABELS = { employment: 'Contract', client_relations: 'Policy' };
const categoryLabel = (c) =>
  CATEGORY_LABELS[c] ||
  (c ? c.replace(/_/g, ' ').replace(/^\w/, (ch) => ch.toUpperCase()) : '');

const TemplateCustomization = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [requirements, setRequirements] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (templateId) {
      loadTemplate(templateId);
    } else {
      setError('No template specified');
      setLoading(false);
    }
  }, [isAuthenticated, templateId, navigate]);

  const loadTemplate = async (templateId) => {
    try {
      setLoading(true);
      
      // ✅ CRITICAL FIX: Get token and add Authorization header
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        console.error('No access token found');
        navigate('/login');
        return;
      }

      const response = await fetch(`/api/documents/template/${templateId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`  // ✅ Added!
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load template');
      }

      const data = await response.json();
      
      if (data.success) {
        setTemplate(data.template);
        loadRequirements(templateId, token);
      } else {
        throw new Error(data.error || 'Failed to load template');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // What would this document leave blank for this salon right now? (best effort: never blocks the page)
  const loadRequirements = async (id, token) => {
    try {
      const response = await fetch(`/api/documents/template/${id}/requirements`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) setRequirements(data);
    } catch (e) {
      console.error('Could not check document requirements:', e);
    }
  };

  const groupByGroup = (fields) => {
    const groups = {};
    (fields || []).forEach((f) => {
      (groups[f.group] = groups[f.group] || []).push(f.label);
    });
    return groups;
  };

  const handleGenerate = async () => {
    setGenerating(true);

    try {
      // ✅ CRITICAL FIX: Get token and add Authorization header
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        console.error('No access token found');
        alert('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }

      const response = await fetch('/api/documents/generate-documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`  // ✅ Added!
        },
        credentials: 'include',
        body: JSON.stringify({
          template_id: template.id,
          customization_data: {
            SALON_NAME: user?.salon_name || '',
            OWNER_NAME: user?.name || '',
            SALON_ADDRESS: '',
            SALON_PHONE: '',
            SALON_EMAIL: user?.email || ''
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        const left = (result.missing_fields || []).map((f) => f.label);
        const note = left.length
          ? `\n\nThese were left as blank lines to fill in by hand:\n- ${left.join('\n- ')}\n\nAdd them in Settings > Documents and generate again to have them filled in automatically.`
          : '';
        alert(`Document "${result.template_name}" generated successfully and saved to your vault!${note}`);
        navigate('/documents');
      } else {
        alert('Error generating document: ' + result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to generate document. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{
            color: '#6b7280',
            margin: 0
          }}>Loading template...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            color: '#dc2626',
            marginBottom: '16px'
          }}>
            <svg
              style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 16px',
                display: 'block'
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '8px'
          }}>Error Loading Template</h2>
          <p style={{
            color: '#6b7280',
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>{error}</p>
          <button
            onClick={() => navigate('/documents/templates')}
            style={{
              color: '#2563eb',
              fontWeight: '500',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
            onMouseOut={(e) => e.target.style.color = '#2563eb'}
          >
            ← Back to Templates
          </button>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{
            color: '#6b7280',
            margin: '0 0 16px 0'
          }}>Template not found</p>
          <button
            onClick={() => navigate('/documents/templates')}
            style={{
              marginTop: '16px',
              color: '#2563eb',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
            onMouseOut={(e) => e.target.style.color = '#2563eb'}
          >
            ← Back to Templates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f9fafb',
      padding: '32px 0',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        maxWidth: '768px',
        margin: '0 auto',
        padding: '0 16px'
      }}>
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/documents/templates')}
            style={{
              color: '#2563eb',
              marginBottom: '16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
            onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
            onMouseOut={(e) => e.target.style.color = '#2563eb'}
          >
            ← Back to Templates
          </button>
          
          <h1 style={{
            fontSize: '30px',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '8px',
            margin: '0'
          }}>Generate Document</h1>
          <p style={{
            color: '#6b7280',
            margin: '8px 0 0 0'
          }}>Welcome, {user?.name || 'User'}</p>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: '#dbeafe',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg
                style={{
                  width: '32px',
                  height: '32px',
                  color: '#2563eb'
                }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 4px 0'
              }}>{template.name}</h2>
              <p style={{
                color: '#6b7280',
                marginTop: '4px',
                margin: '0 0 12px 0'
              }}>{template.description}</p>
              <div style={{
                marginTop: '12px',
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  background: '#f3f4f6',
                  color: '#374151',
                  fontSize: '14px',
                  borderRadius: '4px'
                }}>
                  {template.template_key || template.id}
                </span>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  background: '#dbeafe',
                  color: '#1e40af',
                  fontSize: '14px',
                  borderRadius: '4px'
                }}>
                  {categoryLabel(template.category)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {user && (
          <div style={{
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '16px',
              margin: '0 0 16px 0'
            }}>Document will be customized with:</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>Salon Name</label>
                <p style={{
                  marginTop: '4px',
                  color: '#111827',
                  margin: 0
                }}>{user.salon_name || 'Not provided'}</p>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px'
                }}>Email</label>
                <p style={{
                  marginTop: '4px',
                  color: '#111827',
                  margin: 0
                }}>{user.email}</p>
              </div>
            </div>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: '#eff6ff',
              borderRadius: '8px'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#1e40af',
                margin: 0
              }}>
                ℹ️ Additional salon details from your account will be automatically included in the document.
              </p>
            </div>
          </div>
        )}

        {requirements && requirements.missing_settings && requirements.missing_settings.length > 0 && (
          <div style={{
            background: '#fffbeb',
            borderRadius: '8px',
            border: '1px solid #fcd34d',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#92400e', margin: '0 0 8px 0' }}>
              This document needs {requirements.missing_settings.length} more detail{requirements.missing_settings.length === 1 ? '' : 's'} from you
            </h3>
            <p style={{ color: '#78350f', fontSize: '14px', margin: '0 0 12px 0' }}>
              Add these once in Settings &gt; Documents and every document will use them automatically.
              If you generate now, they will appear as blank lines to fill in by hand.
            </p>
            {Object.entries(groupByGroup(requirements.missing_settings)).map(([group, labels]) => (
              <div key={group} style={{ marginBottom: '8px' }}>
                <strong style={{ fontSize: '14px', color: '#78350f' }}>{group}: </strong>
                <span style={{ fontSize: '14px', color: '#78350f' }}>{labels.join(', ')}</span>
              </div>
            ))}
            <button
              onClick={() => navigate(SETTINGS_DOCUMENTS_PATH)}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#d97706',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '14px'
              }}
            >
              Complete my settings
            </button>
          </div>
        )}

        {requirements && requirements.missing_per_document && requirements.missing_per_document.length > 0 && (
          <div style={{
            background: '#f3f4f6',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            padding: '16px 24px',
            marginBottom: '24px'
          }}>
            <p style={{ color: '#374151', fontSize: '14px', margin: 0 }}>
              <strong>To fill in on the document itself:</strong>{' '}
              {requirements.missing_per_document.map((f) => f.label).join(', ')}
            </p>
          </div>
        )}

        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          padding: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
            flexWrap: 'wrap'
          }}>
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 4px 0'
              }}>Ready to Generate</h3>
              <p style={{
                color: '#6b7280',
                margin: 0
              }}>The document will be customized with your salon details and saved to your vault.</p>
            </div>
            
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                padding: '12px 24px',
                background: generating ? '#9ca3af' : '#2563eb',
                color: 'white',
                borderRadius: '8px',
                border: 'none',
                cursor: generating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                opacity: generating ? 0.5 : 1
              }}
              onMouseOver={(e) => {
                if (!generating) e.target.style.background = '#1d4ed8';
              }}
              onMouseOut={(e) => {
                if (!generating) e.target.style.background = '#2563eb';
              }}
            >
              {generating ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <svg
                    style={{
                      width: '16px',
                      height: '16px'
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>Generate Document</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div style={{
          marginTop: '24px',
          textAlign: 'center'
        }}>
          <button
            onClick={() => navigate('/documents')}
            style={{
              color: '#6b7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
            onMouseOver={(e) => e.target.style.color = '#374151'}
            onMouseOut={(e) => e.target.style.color = '#6b7280'}
          >
            Cancel and return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateCustomization;